import {
  AppStatusIncidentCriteria,
  AppStatusIncidentSeverity,
  AppStatusIncidentStatus,
} from '@src/main/modules/app_status/domain/appStatusIncident.model';
import { toLocalISODate } from '@src/main/shared/utils/dateUtils';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import {
  DataTableFilterMeta,
  DataTableFilterMetaData,
  DataTableOperatorFilterMetaData,
} from 'primereact/datatable';

type ConstraintFilter<T> = {
  operator: FilterOperator;
  constraints: Array<{ value: T; matchMode: FilterMatchMode }>;
};

type ValueFilter<T> = {
  value: T;
  matchMode: FilterMatchMode;
};

export type AppStatusFilterMeta = DataTableFilterMeta & {
  code: ConstraintFilter<string | null>;
  date: ConstraintFilter<[Date | null, Date | null] | null>;
  errorDescription: ConstraintFilter<string | null>;
  possibleSolution: ConstraintFilter<string | null>;
  public: ValueFilter<boolean | null>;
  visibleApp: ValueFilter<boolean | null>;
  severity: ValueFilter<AppStatusIncidentSeverity | null>;
  status: ValueFilter<AppStatusIncidentStatus | null>;
  summary: ConstraintFilter<string | null>;
  title: ConstraintFilter<string | null>;
  version: ConstraintFilter<string | null>;
};

const getFilterValue = (filter?: DataTableFilterMetaData | DataTableOperatorFilterMetaData) => {
  if (!filter) return null;
  return 'constraints' in filter ? filter.constraints?.[0]?.value : filter.value;
};

const getTextFilterValue = (filter?: DataTableFilterMetaData | DataTableOperatorFilterMetaData) => {
  const value = getFilterValue(filter);
  return typeof value === 'string' && value.trim() ? value.trim() : null;
};

export const mapAppStatusFiltersToCriteria = (
  filters: AppStatusFilterMeta,
): AppStatusIncidentCriteria => {
  const dateRange = getFilterValue(filters.date);
  const publiclyVisible = getFilterValue(filters.public);
  const visibleApp = getFilterValue(filters.visibleApp);

  return {
    code: getTextFilterValue(filters.code),
    dateSince: Array.isArray(dateRange) && dateRange[0] ? toLocalISODate(dateRange[0]) : null,
    dateUntil: Array.isArray(dateRange) && dateRange[1] ? toLocalISODate(dateRange[1]) : null,
    errorDescription: getTextFilterValue(filters.errorDescription),
    possibleSolution: getTextFilterValue(filters.possibleSolution),
    public: typeof publiclyVisible === 'boolean' ? publiclyVisible : null,
    visibleApp: typeof visibleApp === 'boolean' ? visibleApp : null,
    severity: getFilterValue(filters.severity) ?? null,
    status: getFilterValue(filters.status) ?? null,
    summary: getTextFilterValue(filters.summary),
    title: getTextFilterValue(filters.title),
    version: getTextFilterValue(filters.version),
  };
};
